import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageCircle, ShieldCheck, X } from 'lucide-react';
import { phoneApi, errorDetailText } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { Card } from '.';

interface PhoneVerifyModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after successful verification (e.g. to retry the blocked action). */
  onVerified: () => void;
}

/**
 * WhatsApp-OTP verification modal — opens when the backend returns the
 * free-tier phone_verification_required 403, and from Settings.
 * Portaled to <body> (house rule: animated ancestors break position:fixed).
 */
export function PhoneVerifyModal({ open, onClose, onVerified }: PhoneVerifyModalProps) {
  const user = useAuthStore((s) => s.user);
  const updateUser = useAuthStore((s) => s.updateUser);

  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  useEffect(() => {
    if (!open) return;
    setStep('phone');
    setPhone(user?.phone?.replace(/^\+91/, '') ?? '');
    setCode('');
    setError('');
    setCooldown(0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => clearInterval(timerRef.current), []);

  const startCooldown = () => {
    setCooldown(60);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) clearInterval(timerRef.current);
        return s - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (busy || cooldown > 0) return;
    setBusy(true);
    setError('');
    try {
      await phoneApi.sendOtp(phone);
      setStep('code');
      setCode('');
      startCooldown();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not send the code. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (busy || code.length !== 6) return;
    setBusy(true);
    setError('');
    try {
      const { data } = await phoneApi.verifyOtp(phone, code);
      updateUser(data);
      onVerified();
    } catch (err: any) {
      setError(errorDetailText(err, 'Verification failed. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[80] bg-legal-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Verify your phone number"
    >
      <Card
        padded={false}
        className="w-full max-w-sm rounded-b-none sm:rounded-card"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-legal-100">
          <h3 className="flex items-center gap-2 font-semibold text-legal-900">
            <MessageCircle className="h-4 w-4 text-emerald-600" />
            Verify on WhatsApp
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-legal-400 hover:text-legal-700 hover:bg-legal-50 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <p className="text-sm text-legal-500">
            {step === 'phone'
              ? 'Verify your number once — it also connects the Urava WhatsApp bot. We’ll send a 6-digit code to your WhatsApp.'
              : `Enter the code we sent on WhatsApp to +91 ${phone.replace(/\D/g, '').slice(-10)}.`}
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
              {error}
            </div>
          )}

          {step === 'phone' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                sendCode();
              }}
            >
              <label className="block text-xs font-medium text-legal-700 mb-1.5">
                WhatsApp number
              </label>
              <div className="flex items-stretch gap-2">
                <span className="inline-flex items-center px-3 border border-legal-300 rounded-lg text-sm text-legal-500 bg-legal-50">
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoFocus
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                  placeholder="98765 43210"
                  className="flex-1 min-w-0 px-3 py-2 border border-legal-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>
              <button
                type="submit"
                disabled={busy || phone.replace(/\D/g, '').length < 10}
                className="mt-4 w-full px-4 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-50"
              >
                {busy ? 'Sending…' : 'Send code on WhatsApp'}
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                verifyCode();
              }}
            >
              <label className="block text-xs font-medium text-legal-700 mb-1.5">
                6-digit code
              </label>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
                className="w-full px-3 py-2 border border-legal-300 rounded-lg text-center text-lg tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent"
              />
              <button
                type="submit"
                disabled={busy || code.length !== 6}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-50"
              >
                <ShieldCheck className="h-4 w-4" />
                {busy ? 'Verifying…' : 'Verify'}
              </button>
              <div className="mt-3 flex items-center justify-between text-xs text-legal-400">
                <button
                  type="button"
                  onClick={() => setStep('phone')}
                  className="hover:text-legal-700 transition-colors"
                >
                  Change number
                </button>
                <button
                  type="button"
                  onClick={sendCode}
                  disabled={cooldown > 0 || busy}
                  className="hover:text-legal-700 transition-colors disabled:opacity-50"
                >
                  {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            </form>
          )}
        </div>
      </Card>
    </div>,
    document.body
  );
}
